import React, { useState, useMemo, useRef } from "react";
import { FiRefreshCcw, FiEdit3, FiInfo, FiXCircle } from "react-icons/fi";
import { useGetPreInvoicesQuery, useDeletePreInvoiceMutation } from "../redux/features/apiSlices/invoices/preInvoice"
import { useSelector } from 'react-redux';
import DeleteConfirmation from './DeleteConfirmation';

const PreInvoices = ({ onModify }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showPrintInvoice, setShowPrintInvoice] = useState(false);
  const [showThermalPreview, setShowThermalPreview] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const printInvoiceRef = useRef();

  const { data: preInvoices, isLoading, refetch } = useGetPreInvoicesQuery();
  const [deletePreInvoice] = useDeletePreInvoiceMutation();
  const { company } = useSelector((state) => state.setup);
  const { user } = useSelector((state) => state.user);

  const filteredOrders = useMemo(() => {
    if (!preInvoices) return [];
    return preInvoices?.filter(order => {
      const searchLower = searchQuery.toLowerCase();
      return order?.billNumber?.toLowerCase().includes(searchLower);
    });
  }, [preInvoices, searchQuery]);

  const handleCancelOrderClick = () => {
    if (selectedOrder) {
      setIsDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedOrder) return;
    try {
      await deletePreInvoice(selectedOrder.id).unwrap();
      setSelectedOrder(null);
      refetch();
    } catch (error) {
      console.error("Failed to cancel order:", error);
      alert("Failed to cancel order");
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <div className="h-screen overflow-y-hidden flex w-full">
      <div className="w-full bg-white rounded-xl shadow-md flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-md font-semibold text-gray-700">
            Pre Paid Bills ({filteredOrders?.length})
          </h2>
          <button onClick={() => refetch()} className="cursor-pointer hover:bg-gray-200 p-2 rounded-lg transition-colors" title="Refresh">
            <FiRefreshCcw size={20} className="text-gray-600" />
          </button>
        </div>

        <div className="p-4">
          <input type="text" placeholder="Search by Bill Number" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full px-3 py-2 rounded-lg border bg-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-100 mx-4 rounded-lg">
          {isLoading ? (
            <div className="p-4 text-center text-gray-600"><p>Loading orders...</p></div>
          ) : filteredOrders?.length === 0 ? (
            <div className="p-4 text-center text-gray-500"><p>No pending orders found</p></div>
          ) : (
            <div className="space-y-2 p-2">
              {filteredOrders?.map((order) => (
                <div key={order.id} onClick={() => setSelectedOrder(order)} className={`p-3 rounded-lg cursor-pointer transition-all ${selectedOrder?.id === order.id ? 'bg-blue-500 text-white shadow-lg' : 'bg-white hover:bg-blue-50 border border-gray-200'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-sm">{order?.billNumber || order?.USIN}</p>
                      <p className="text-xs opacity-75">{order?.BuyerName}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm">{order?.TotalBillAmount?.toFixed(2) || '0.00'}</p>
                      <p className="text-xs opacity-75">{new Date(order?.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2 text-xs">
                    <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded">{order?.status || 'pending'}</span>
                    <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded">Items: {order?.TotalQuantity || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedOrder && (
          <div className="p-4 space-y-3 border-t">
            <button onClick={() => { if (onModify) { onModify(selectedOrder); } }} className="w-full bg-blue-100 text-blue-700 text-xs py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-200 transition-colors">
              Modify Order
            </button>
            {/* <button onClick={() => setShowOrderDetails(true)} className="w-full bg-gray-100 text-gray-800 text-xs py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors">
              <FiInfo size={16} /> Order Details
            </button> */}
            <div className="flex gap-3 text-xs">
              {/* <button className="flex-1 bg-green-100 text-green-700 py-2 rounded-lg hover:bg-green-200 transition-colors font-semibold">📄 Invoice</button> */}
              <button onClick={() => setShowPrintInvoice(true)} className="flex-1 bg-purple-100 text-purple-700 py-2 rounded-lg hover:bg-purple-200 transition-colors">Order Detail</button>
            </div>
            <button onClick={handleCancelOrderClick} className="w-full bg-red-100 text-red-600 py-2 rounded-lg text-xs flex items-center justify-center gap-2 hover:bg-red-200 transition-colors">
              <FiXCircle size={16} /> Cancel Order
            </button>
          </div>
        )}
      </div>

      {showPrintInvoice && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-[210mm] max-h-[95vh] overflow-y-auto p-8" id="printable-area">
            <div className="text-center mb-8">
              <h1 className="font-bold text-4xl uppercase mb-3">Pre Paid Bill</h1>
              <p className="text-xl font-semibold">Bill #: {selectedOrder?.billNumber || selectedOrder?.USIN}</p>
            </div>
            <table className="w-full text-lg border-2 border-black">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border-2 border-black px-4 py-3 text-left">S#</th>
                  <th className="border-2 border-black px-4 py-3 text-left">Product Name</th>
                  <th className="border-2 border-black px-4 py-3 text-center">Quantity</th>
                </tr>
              </thead>
              <tbody>
                {(selectedOrder?.products || []).map((product, index) => (
                  <tr key={index}>
                    <td className="border-2 border-black px-4 py-3">{index + 1}</td>
                    <td className="border-2 border-black px-4 py-3">{product.ItemName || product.productname}</td>
                    <td className="border-2 border-black px-4 py-3 text-center">{product.quantity || 1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-3 mt-6 no-print">
              <button onClick={() => window.print()} className="bg-blue-600 text-white px-6 py-3 rounded shadow text-sm font-semibold hover:bg-blue-700">🖨️ Print A4</button>
              <button onClick={() => setShowThermalPreview(true)} className="bg-green-600 text-white px-6 py-3 rounded shadow text-sm font-semibold hover:bg-green-700">🖨️ Thermal Print</button>
              <button onClick={() => setShowPrintInvoice(false)} className="bg-red-500 text-white px-6 py-3 rounded shadow text-sm font-semibold hover:bg-red-600">✖ Close</button>
            </div>
          </div>
        </div>
      )}

      {showThermalPreview && selectedOrder && (
        <div className="fixed inset-0 bg-white flex items-center justify-center z-50 p-4">
          <div className="bg-white w-[280px] rounded-lg shadow-2xl p-3">
            <div ref={printInvoiceRef}></div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { const w = window.open('', '', 'width=320,height=600'); w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Receipt</title><style>@page{size:80mm auto;margin:0}body{font-family:'Courier New',monospace;font-size:10pt;width:76mm;margin:0;padding:5mm;font-weight:bold}.text-center{text-align:center}.border-b{border-bottom:1px solid black;padding-bottom:3mm;margin-bottom:3mm}table{width:100%;border-collapse:collapse;font-size:9pt;margin-top:3mm}th,td{padding:2mm;border:1px solid black;text-align:left}th{background-color:#f0f0f0}@media print{body{-webkit-print-color-adjust:exact}}</style></head><body><div class="text-center border-b"><h1 style="font-size:14pt;margin:0">Pre Paid Bill</h1><p style="font-size:10pt;margin:2mm 0 0 0">Bill #: ${selectedOrder?.billNumber || selectedOrder?.USIN}</p></div><table><thead><tr><th style="width:15%">S#</th><th style="width:60%">Product Name</th><th style="width:25%;text-align:center">Quantity</th></tr></thead><tbody>${(selectedOrder?.products || []).map((item, i) => `<tr><td>${i + 1}</td><td>${item.ItemName || item.productname}</td><td style="text-align:center">${item.quantity || 1}</td></tr>`).join('')}</tbody></table></body></html>`); w.document.close(); w.focus(); setTimeout(() => { w.print(); w.close(); }, 250); }} className="flex-1 bg-green-600 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-green-700">🖨️ Print</button>
              <button onClick={() => { setShowThermalPreview(false); setShowPrintInvoice(false); }} className="flex-1 bg-red-500 text-white px-3 py-2 rounded text-xs font-semibold hover:bg-red-600">✖ Close</button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      <style>{`@media print { body * { visibility: hidden; } #printable-area, #printable-area * { visibility: visible; } #printable-area { position: absolute; left: 0; top: 0; width: 100%; } .no-print { display: none !important; } @page { size: A4; margin: 15mm; }}`}</style>
    </div>
  );
};

export default PreInvoices;
